import React, { Fragment, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import useDebounce from "react-use/esm/useDebounce";
import { GenericAvatar } from "../generic/GenericAvatar";
import { formatDate } from "../../helpers/dateHelper";
import { DECLINE, EMAIL_VERIFIED, USER_STATUS } from "../../enums/statusEnums";
import { approveUser } from "../../config/apisauce";
import { DECLINE_CHECKLIST } from "../../enums/generalEnums";
import { ReasonDialog } from "../../pages/admin/dialogs/ReasonDialog";
import useNotiStack from "./../../hooks/useNotiStack";
import { ProfileDetails } from "../../pages/admin/adminHomePage/profileDetails/ProfileDetails";
import { GenericDialog } from "../generic/GenericDialog";
import { GenericCircleIcon } from "../generic/GenericCircleIcon";
import { kebabToSentence } from "../../helpers/stringHelper";
import createUsersQueryOptions from "../../queryOptions/createUsersQueryOptions";
import { ROLES } from "../../enums/RoleEnum";
import CustomDataGrid, { CustomNoResultsOverlay } from "./CustomDataGrid";
import { PAGE_OPTIONS } from "../../enums/dataGridEnums";
import { router } from "@inertiajs/react";
import { DAY_ONLY_FORMAT } from "../../enums/dateEnums";

export const IncompleteProfileTable = ({ minified = false }) => {
  const { success } = useNotiStack();
  const [openReasonDialog, setOpenReasonDialog] = useState(false);
  const [openProfileDetailsDialog, setOpenProfileDetailsDialog] = useState(false);
  const [dialogDetails, setDialogDetails] = useState({});
  const [details, setDetails] = useState("");
  const [stillProcessing, setStillProcessing] = useState([]);
  const [userId, setUserId] = useState(null);
  const [acceptConfirmationState, setAcceptConfirmationState] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const genericColumnProps = {
    editable: false,
    sortable: false,
    resizable: false,
  };

  const handleOpenProfileDetailsDialog = (id) => {
    setUserId(id);
    setOpenProfileDetailsDialog(true);
  };

  const handleCloseProfileDetailsDialog = () => {
    setUserId(null);
    setOpenProfileDetailsDialog(false);
  };

  const handleOpenDialog = (label, data, checklists, action) => {
    setDialogDetails({ label: label, data: data, checklists: checklists, action: action });
    setOpenReasonDialog(true);
    setOpenProfileDetailsDialog(false);
  };

  const handleCloseReasonDialog = () => {
    setOpenReasonDialog(false);
    setDialogDetails({});
    refetch();
  };

  const handleAcceptUser = async (values) => {
    setStillProcessing((prev) => [...prev, values?.id]);
    const params = {
      user_id: values?.id,
      email: values?.email,
      name: values?.name,
    };
    const { ok } = await approveUser(params);
    if (ok) {
      success("User has been accepted!");
      refetch();
    }
    setAcceptConfirmationState(false);
    setStillProcessing((prev) => prev?.filter((item) => item !== values?.id));
  };

  const handleAcceptChecker = (values) => {
    setAcceptConfirmationState(true);
    setSelectedCreator(values);
  };

  const [keyword, setKeyword] = useState("");
  const [tableState, setTableState] = useState({
    filter: {
      search: "",
      status: [USER_STATUS.EMAIL_VERIFIED, USER_STATUS.UNVERIFIED, USER_STATUS.ONDATO_REJECTED],
      "roles.name": [ROLES.CREATOR_ROLE, ROLES.PENDING_CREATOR_ROLE],
    },
    sort: ["-created_at"],
    include: ["details", "details.profilePicture"],
    pageSize: minified ? 5 : 10,
    page: 1,
  });

  const { data: users, isFetching, refetch } = useQuery(createUsersQueryOptions(tableState));

  const columns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      flex: 1.2,
      renderCell: ({ row }) => {
        return (
          <Stack direction={"row"} alignItems={"center"} gap={1.5} mt={0.5}>
            <GenericAvatar
              location={row?.details?.profile_picture?.location}
              avatarId={row?.id}
              size={"42px"}
              backgroundColor={"background.default"}
            />
            {row?.name ? (
              <Typography fontWeight={700}>{row?.name}</Typography>
            ) : (
              <Typography color="grey.light">N/A</Typography>
            )}
          </Stack>
        );
      },
      ...genericColumnProps,
    },
    {
      field: "profession",
      headerName: "Profession",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) =>
        row?.profession ?? (
          <Typography mt={1.7} color="grey.light">
            N/A
          </Typography>
        ),
      ...genericColumnProps,
    },
    {
      field: "created_at",
      headerName: "Request Date",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => formatDate(new Date(row?.created_at)),
      ...genericColumnProps,
    },
    {
      field: "referral_code",
      headerName: "Referral Code",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) =>
        row?.details?.meta?.reffered_by ?? (
          <Typography mt={1.7} color="grey.light">
            N/A
          </Typography>
        ),
      ...genericColumnProps,
    },
    {
      field: "current_step",
      headerName: "Current Step",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => (
        <Typography mt={1.7} textTransform={"capitalize"}>
          {kebabToSentence(row?.details?.current_step) || "Initial Signup"}
        </Typography>
      ),
      ...genericColumnProps,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography mt={1.7} fontWeight={700} textTransform={"capitalize"} color={"error"}>
            {row?.status === EMAIL_VERIFIED ? "Ondato Unverified" : "Email Unverified"}
          </Typography>
        );
      },
      ...genericColumnProps,
    },
    {
      field: "details",
      headerName: "Details",
      minWidth: 140,
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack gap={1} direction={"row"} mt={1.1}>
            <Button
              fullWidth
              variant="contained"
              sx={{ color: "#FFF" }}
              onClick={() => handleOpenProfileDetailsDialog(row?.id)}
            >
              Details
            </Button>
          </Stack>
        );
      },
      ...genericColumnProps,
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 280,
      flex: 1.4,
      renderCell: ({ row }) => {
        const processing = stillProcessing?.includes(row?.id);
        return (
          <Stack gap={1} direction={"row"} mt={1.1}>
            <Button
              disabled={processing}
              onClick={() => handleOpenDialog("Declining", row, DECLINE_CHECKLIST, DECLINE)}
              fullWidth
              variant="contained"
              color="error"
              sx={{ color: "#FFF" }}
            >
              Decline
            </Button>
            {row.details.current_step !== null && (
              <LoadingButton
                fullWidth
                onClick={() => handleAcceptChecker(row)}
                size="small"
                loading={processing}
                variant="contained"
                disabled={processing}
                color={"warning"}
              >
                Accept
              </LoadingButton>
            )}
          </Stack>
        );
      },
      ...genericColumnProps,
    },
  ];

  const [,] = useDebounce(
    () => {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          search: keyword,
        },
        page: 1,
      }));
    },
    1000,
    [keyword]
  );

  return (
    <Fragment>
      <Card sx={{ boxShadow: "none" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xxs: "column", mds: "row" },
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="primary">
              Incomplete Profiles
            </Typography>

            <Typography fontWeight={500}>
              These accounts have started the account creation process but have not complete their profiles and verifications.
            </Typography>
          </Box>

          <Box width={{ xxs: "100%", mds: minified ? "130px" : "350px" }}>
            {minified && (
              <Grid container item justifyContent={"flex-end"} size={{ xs: 12, sm: 6 }}>
                <Button
                  variant="outlined"
                  sx={{ width: { xs: "100%", sm: "102px" } }}
                  onClick={() => router.visit("/admin-collab-creators")}
                >
                  See All
                </Button>
              </Grid>
            )}
            {!minified && (
              <FormControl fullWidth>
                <OutlinedInput
                  fullWidth
                  variant="outlined"
                  placeholder="Search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <MagnifyingGlass />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            )}
          </Box>
        </Box>

        {/* MOBILE VERSION */}
        <Box sx={{ display: { xxs: "flex", xsm: "none" }, flexDirection: "column", width: "100%" }}>
          <CreatorCardListMobile
            creators={users}
            setTableState={setTableState}
            minified={minified}
            handleOpenProfileDetailsDialog={handleOpenProfileDetailsDialog}
            handleOpenDialog={handleOpenDialog}
            handleAcceptChecker={handleAcceptChecker}
          />
        </Box>

        {/* TABLE */}
        <Box height={minified ? "325px" : "635px"} sx={{ display: { xxs: "none", xsm: "block" }, overflowX: "auto" }}>
          <CustomDataGrid
            columns={columns}
            pageSizeOptions={PAGE_OPTIONS}
            rows={users?.data ?? []}
            rowCount={users?.total ?? 0}
            paginationMode="server"
            paginationModel={{
              pageSize: users?.per_page ?? 10,
              page: typeof users?.current_page === "number" ? users.current_page - 1 : 0,
            }}
            onPaginationModelChange={({ page, pageSize }) => {
              setTableState((prev) => ({
                ...prev,
                page: pageSize !== prev.pageSize ? 1 : page + 1,
                pageSize,
              }));
            }}
            slotProps={{
              pagination: {
                sx: {
                  "& .MuiTablePagination-selectLabel": {
                    display: "block",
                  },
                  "& .MuiTablePagination-input": {
                    display: "inline-flex",
                  },
                },
              },
            }}
            hideFooter={minified}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            checkboxSelection={false}
            loading={!users || isFetching}
          />
        </Box>
      </Card>
      <ReasonDialog
        details={details}
        setDetails={setDetails}
        dialogDetails={dialogDetails}
        openDialog={openReasonDialog}
        handleCloseDialog={() => handleCloseReasonDialog()}
      />
      <ProfileDetails
        isBypassOndato={true}
        openDialog={openProfileDetailsDialog}
        userId={userId}
        handleClose={handleCloseProfileDetailsDialog}
        handleOpenReasonDialog={handleOpenDialog}
        handleAcceptUser={handleAcceptChecker}
        refetchCreatorList={() => refetch()}
      />
      <GenericDialog
        title={"Bypass Verification"}
        width={"550px"}
        openDialog={acceptConfirmationState}
        handleClose={() => setAcceptConfirmationState(false)}
      >
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"} mb={2}>
          <GenericCircleIcon color={"primary.main"}>
            <Warning size={96} weight="thin" />
          </GenericCircleIcon>
        </Box>
        <Stack gap={3} alignItems={"center"}>
          <Typography variant="subtitle1" textAlign={"center"}>
            You are about to accept a user{" "}
            <b>{selectedCreator?.status === EMAIL_VERIFIED ? "without Ondato verification." : "Ondato Rejected."}</b>
            <br></br>
            Are you sure you want to continue?
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} gap={1} mt={2}>
          <Button
            variant="outlined"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            onClick={() => setAcceptConfirmationState(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            onClick={() => {
              handleAcceptUser(selectedCreator);
              setSelectedCreator(null);
              setAcceptConfirmationState(false);
            }}
          >
            Approve Anyway
          </Button>
        </Stack>
      </GenericDialog>
    </Fragment>
  );
};

const CreatorCardListMobile = ({
  creators,
  setTableState,
  handleOpenProfileDetailsDialog,
  handleOpenDialog,
  handleAcceptChecker,
  minified,
}) => {
  const creatorList = useMemo(() => creators?.data || [], [creators]);

  if (!creatorList.length) {
    return (
      <Box py={2} textAlign="center">
        <CustomNoResultsOverlay />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          height: "680px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {creatorList.map((creator, index) => (
          <Box key={`creator-list-card-${index}`}>
            <CreatorCardMobile
              creator={creator}
              handleOpenProfileDetailsDialog={handleOpenProfileDetailsDialog}
              handleOpenDialog={handleOpenDialog}
              handleAcceptChecker={handleAcceptChecker}
            />
            {index !== creatorList.length - 1 && <Divider sx={{ borderColor: "borders.main" }} />}
          </Box>
        ))}
      </Box>

      {!minified && (
        <Box>
          <TablePagination
            labelRowsPerPage="Per page:"
            component="div"
            count={creators.total}
            page={creators.current_page - 1}
            rowsPerPageOptions={PAGE_OPTIONS}
            onPageChange={(event, newPage) =>
              setTableState((prev) => ({
                ...prev,
                page: newPage + 1,
              }))
            }
            rowsPerPage={creators.per_page}
            onRowsPerPageChange={(event) =>
              setTableState((prev) => ({
                ...prev,
                pageSize: parseInt(event.target.value, 10),
                page: 1,
              }))
            }
          />
        </Box>
      )}
    </Box>
  );
};

const CreatorCardMobile = ({ creator, handleOpenProfileDetailsDialog, handleOpenDialog, handleAcceptChecker }) => {
  return (
    <Box sx={{ padding: 2, pr: 1 }}>
      <Grid container>
        <Grid container item size={10} alignItems={"center"}>
          <Grid item size={5}>
            <GenericAvatar
              location={creator?.details?.profile_picture?.location}
              avatarId={creator?.id}
              size={"92px"}
              backgroundColor={"background.default"}
            />
          </Grid>
          <Grid item size={7}>
            <Stack>
              {creator?.name ? (
                <Typography fontWeight={700}>{creator?.name}</Typography>
              ) : (
                <Typography color="grey.light">N/A</Typography>
              )}
              <Typography noWrap>{creator?.details?.profession}</Typography>
              <Typography fontWeight={700} textTransform={"capitalize"} color={"error"}>
                {creator?.status === EMAIL_VERIFIED ? "Ondato Unverified" : "Email Unverified"}
              </Typography>
              {kebabToSentence(creator.details.current_step) || "Initial Signup"}
            </Stack>
          </Grid>
        </Grid>
        <Grid container item justifyContent={"flex-end"} size={2}>
          <Typography color="text.light">{formatDate(new Date(creator?.created_at), DAY_ONLY_FORMAT)}</Typography>
        </Grid>
      </Grid>
      <Stack direction={"row"} gap={0.5} mt={1.5}>
        <Button
          fullWidth
          variant="contained"
          color="error"
          onClick={() => handleOpenDialog("Declining", creator, DECLINE_CHECKLIST, DECLINE)}
        >
          Decline
        </Button>
        {creator.details.current_step && (
          <Button fullWidth variant="contained" color={"warning"} onClick={() => handleAcceptChecker(creator)}>
            Accept
          </Button>
        )}
      </Stack>
      <Stack direction={"column"} mt={1.5} gap={1.5}>
        <Button fullWidth variant="contained" onClick={() => handleOpenProfileDetailsDialog(creator?.id)}>
          Details
        </Button>
      </Stack>
    </Box>
  );
};
